import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Empty, Input, type TableColumnsType, type TableProps } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useGetCustomerUsersQuery, useDeleteCustomerUserMutation } from '@api/users';
import { useCustomerPermissions } from '../../userManagementPermissions';
import RoutePaths from '@routePaths';
import { usePagination } from '@shared/common/hooks/usePagination';
import Button, { ButtonTag, ButtonVariant } from '@shared/components/Button/Button';
import Modal, { ModalSize } from '@shared/components/Modal/Modal';
import DataTablePanel from '@shared/components/DataTablePanel/DataTablePanel';
import dashboardStyles from '@shared/components/Dashboard/Dashboard.module.scss';
import { Text, Title } from '@shared/components/typography';
import { TitleTag, TitleVariant } from '@shared/components/typography/Title';
import { CustomerUserSortField, UserModel } from '@shared/models/userModels';
import classNames from 'classnames';
import styles from './UserManagement.module.scss';

interface ColumnTextFilterProps extends FilterDropdownProps {
  ariaLabel: string;
  onApply: (value: string) => void;
  searchLabel: string;
  resetLabel: string;
}

const ColumnTextFilter = ({
  ariaLabel,
  clearFilters,
  close,
  confirm,
  onApply,
  resetLabel,
  searchLabel,
  selectedKeys,
  setSelectedKeys,
}: ColumnTextFilterProps) => (
  <div className={styles.columnFilter}>
    <Input
      aria-label={ariaLabel}
      autoFocus
      value={String(selectedKeys[0] ?? '')}
      onChange={(event) => setSelectedKeys(event.target.value ? [event.target.value] : [])}
      onPressEnter={() => {
        onApply(String(selectedKeys[0] ?? '').trim());
        confirm();
      }}
    />
    <div className={styles.columnFilterActions}>
      <Button
        type="button"
        variant={ButtonVariant.Outline}
        onClick={() => {
          clearFilters?.();
          onApply('');
          close();
        }}
      >
        {resetLabel}
      </Button>
      <Button
        type="button"
        onClick={() => {
          onApply(String(selectedKeys[0] ?? '').trim());
          confirm();
        }}
      >
        {searchLabel}
      </Button>
    </div>
  </div>
);

const getSortOrder = (
  sortBy: CustomerUserSortField | undefined,
  sortOrder: 'asc' | 'desc' | undefined,
  field: CustomerUserSortField,
) => sortBy === field ? (sortOrder === 'desc' ? 'descend' as const : 'ascend' as const) : null;

const ManageUsers = () => {
  const { t } = useTranslation();
  const pagination = usePagination(8, 1);
  const { currentPage, pageSize, changeCurrentPage } = pagination;
  const {
    organisationId,
    activeMembership,
    canAddUsers,
    canEditUsers,
    canDeleteUsers,
  } = useCustomerPermissions();
  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [ssoFilter, setSsoFilter] = useState<boolean | undefined>();
  const [sortBy, setSortBy] = useState<CustomerUserSortField | undefined>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<UserModel | null>(null);
  const deleteInFlight = useRef(false);

  const queryArgs = useMemo(() => ({
    organisationId: organisationId ?? '',
    pageSize,
    pageNumber: currentPage,
    name: nameFilter || undefined,
    email: emailFilter || undefined,
    ssoUsers: ssoFilter,
    sortBy,
    sortOrder,
  }), [currentPage, emailFilter, nameFilter, organisationId, pageSize, sortBy, sortOrder, ssoFilter]);

  const { currentData: data, isFetching, isError, refetch } = useGetCustomerUsersQuery(queryArgs, {
    skip: !organisationId,
    refetchOnMountOrArgChange: true,
  });
  const [deleteUser, { isLoading: isDeleting }] = useDeleteCustomerUserMutation();

  useEffect(() => {
    setNameFilter('');
    setEmailFilter('');
    setSsoFilter(undefined);
    setSortBy(undefined);
    setSortOrder(undefined);
    setDeleteTarget(null);
    void changeCurrentPage(1);
  }, [changeCurrentPage, organisationId]);

  const applyFilter = useCallback((setter: (value: string) => void, value: string) => {
    setter(value);
    void changeCurrentPage(1);
  }, [changeCurrentPage]);

  const onTableChange = useCallback<NonNullable<TableProps<UserModel>['onChange']>>((_pagination, filters, sorter, extra) => {
    if (extra.action === 'paginate') return;
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextSortBy = activeSorter.columnKey as CustomerUserSortField | undefined;
    setSortBy(activeSorter.order ? nextSortBy : undefined);
    setSortOrder(activeSorter.order ? (activeSorter.order === 'ascend' ? 'asc' : 'desc') : undefined);
    if (extra.action === 'filter') {
      const sso = filters.sso ?? [];
      setSsoFilter(sso.length === 1 ? sso[0] === 'sso' : undefined);
    }
    void changeCurrentPage(1);
  }, [changeCurrentPage]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget || !organisationId || !canDeleteUsers || deleteInFlight.current || deleteTarget.is_multi_organisation_user) return;
    deleteInFlight.current = true;
    try {
      await deleteUser({ userId: deleteTarget.id, organisationId }).unwrap();
      toast.success(t('userManagement.delete.success'));
      setDeleteTarget(null);
    } catch {
      toast.error(t('userManagement.delete.failure'));
    } finally {
      deleteInFlight.current = false;
    }
  }, [canDeleteUsers, deleteTarget, deleteUser, organisationId, t]);

  const columns = useMemo<TableColumnsType<UserModel>>(() => [
    {
      title: t('userManagement.table.email'),
      dataIndex: 'email',
      key: 'email',
      width: 200,
      ellipsis: true,
      sorter: true,
      sortOrder: getSortOrder(sortBy, sortOrder, 'email'),
      filteredValue: emailFilter ? [emailFilter] : null,
      filterDropdown: (props) => (
        <ColumnTextFilter
          {...props}
          ariaLabel={t('userManagement.table.filterEmail')}
          onApply={(value) => applyFilter(setEmailFilter, value)}
          searchLabel={t('common.search')}
          resetLabel={t('common.reset')}
        />
      ),
    },
    {
      title: t('userManagement.table.userName'),
      dataIndex: 'display_name',
      key: 'display_name',
      width: 160,
      ellipsis: true,
      sorter: true,
      sortOrder: getSortOrder(sortBy, sortOrder, 'display_name'),
      filteredValue: nameFilter ? [nameFilter] : null,
      filterDropdown: (props) => (
        <ColumnTextFilter
          {...props}
          ariaLabel={t('userManagement.table.filterName')}
          onApply={(value) => applyFilter(setNameFilter, value)}
          searchLabel={t('common.search')}
          resetLabel={t('common.reset')}
        />
      ),
    },
    {
      title: t('userManagement.table.organisationName'),
      key: 'organisations',
      width: 180,
      ellipsis: true,
      sorter: true,
      sortOrder: getSortOrder(sortBy, sortOrder, 'organisations'),
      render: (_, user) => activeMembership?.name ?? user.organisations.find(({ id }) => id === organisationId)?.name ?? '',
    },
    {
      title: t('userManagement.table.sso'),
      key: 'sso',
      width: 140,
      render: (_, user) => (!user.has_private_account ? 'SSO' : 'NO SSO'),
      sorter: true,
      sortOrder: getSortOrder(sortBy, sortOrder, 'sso'),
      filters: [
        { text: 'SSO', value: 'sso' },
        { text: 'NO SSO', value: 'no-sso' },
      ],
      filteredValue: ssoFilter === undefined ? null : [ssoFilter ? 'sso' : 'no-sso'],
    },
    {
      title: t('common.edit'),
      key: 'edit',
      width: 80,
      fixed: 'right',
      render: (_, user) => canEditUsers ? (
        <Button
          as={ButtonTag.InternalLink}
          classes={classNames(styles.actionButton, styles.editButton)}
          href={RoutePaths.EditUser.replace(':id', user.id)}
          variant={ButtonVariant.Outline}
        >
          {t('common.edit')}
        </Button>
      ) : (
        <Button classes={classNames(styles.actionButton, styles.editButton)} isDisabled variant={ButtonVariant.Outline}>
          {t('common.edit')}
        </Button>
      ),
    },
    {
      title: t('common.delete'),
      key: 'delete',
      width: 90,
      fixed: 'right',
      render: (_, user) => (
        <Button
          classes={styles.actionButton}
          isDisabled={!canDeleteUsers}
          onClick={() => setDeleteTarget(user)}
        >
          {t('common.delete')}
        </Button>
      ),
    },
  ], [activeMembership?.name, applyFilter, canDeleteUsers, canEditUsers, emailFilter, nameFilter, organisationId, sortBy, sortOrder, ssoFilter, t]);

  const isFiltered = Boolean(nameFilter || emailFilter || ssoFilter !== undefined);
  const emptyText = isError ? (
    <div className={styles.emptyState} role="alert">
      <Text>{t('userManagement.table.loadFailed')}</Text>
      <Button onClick={() => refetch()}>{t('common.retry')}</Button>
    </div>
  ) : <Empty description={isFiltered ? t('userManagement.table.noResults') : t('userManagement.table.empty')} />;

  return (
    <section className={styles.page} aria-labelledby="manage-users-title">
      <Title tag={TitleTag.h1} variant={TitleVariant.main}>
        <span id="manage-users-title">{t('userManagement.manageTitle')}</span>
      </Title>
      <div className={dashboardStyles.wrapper}>
        <div className={dashboardStyles.headerMini}>
          {canAddUsers ? (
            <Button as={ButtonTag.InternalLink} href={RoutePaths.AddUser}>{t('userManagement.add.title')}</Button>
          ) : (
            <Button isDisabled>{t('userManagement.add.title')}</Button>
          )}
        </div>
        <DataTablePanel<UserModel>
          sectionTitle={t('userManagement.table.sectionTitle')}
          totalLabel={isFetching ? t('userManagement.table.loading') : t('userManagement.table.resultCount', { count: data?.total_items ?? 0 })}
          columns={columns}
          data={data?.results ?? []}
          loading={isFetching}
          pagination={pagination}
          totalItems={data?.total_items ?? 0}
          rowKey="id"
          scroll={{ x: 850 }}
          onChange={onTableChange}
          locale={{ emptyText }}
        />
      </div>
      {!!deleteTarget && (
        <Modal
          maxSize={ModalSize.SM}
          closeHandler={() => !isDeleting && setDeleteTarget(null)}
          closeLabel={t('common.close')}
          isOpen
          title={deleteTarget.is_multi_organisation_user
            ? t('userManagement.delete.multiTitle')
            : t('userManagement.delete.title')}
          wrapperClasses={styles.modal}
        >
          {deleteTarget.is_multi_organisation_user ? (
            <Text>
              <strong>{deleteTarget.email}</strong>{' '}
              {t('userManagement.delete.multiBody')}
            </Text>
          ) : (
            <>
              <Text>
                {t('userManagement.delete.bodyPrefix')} <strong>{deleteTarget.email}</strong>{' '}
                {t('userManagement.delete.bodySuffix', { organisation: deleteTarget.organisations[0]?.name ?? '' })}
              </Text>
              <div className={styles.modalActions}>
                <Button
                  variant={ButtonVariant.Outline}
                  isDisabled={isDeleting}
                  onClick={() => setDeleteTarget(null)}
                >
                  {t('common.cancel')}
                </Button>
                <Button isDisabled={isDeleting} onClick={confirmDelete}>
                  {isDeleting ? t('userManagement.delete.pending') : t('userManagement.delete.title')}
                </Button>
              </div>
            </>
          )}
        </Modal>
      )}
    </section>
  );
};

export default ManageUsers;
